<template>
  <div v-show="!isFetchingUser">
    <v-card elevation="0" class="ma-sm-5">
      <v-card-text>
        <v-form ref="form">
          <v-row justify="space-around">
            <v-col cols="12" md="5">
              <h4 class="greyMedium--text">{{ $t('settings/profile:common.profile') }}</h4>

              <!-- Email -->
              <v-text-field
                  outlined
                  clearable
                  v-model="data.email"
                  :label="$t('common:email')"
                  tabindex="1"
              />

              <!-- Phonenumber -->
              <v-text-field
                  outlined
                  clearable
                  v-model="data.phone"
                  :label="$t('settings/profile:common.phone')"
                  tabindex="2"
              />

              <v-btn
                outlined
                :href="passwordChangeRoute"
                target="_blank"
                color="primary"
              >
                {{ $t('settings/profile:common.change-pwd') }}
              </v-btn>
            </v-col>

            <v-col cols="12" md="5">
              <h4 class="greyMedium--text">{{ $t('common:settings') }}</h4>

              <v-select
                  :items="languages"
                  v-model="data.language"
                  outlined
                  :menu-props="{ bottom: true, offsetY: true }"
                  :label="$t('settings/profile:common.language')"
                  tabindex="3"
              />

              <p class="body-1">{{ $t('settings/profile:common.communication') + ':' }}</p>

              <v-checkbox
                  color="secondary"
                  v-model="data.mailNotification"
                  :label="$t('settings/profile:common.mail-notifications')"
                  tabindex="4">
              </v-checkbox>
            </v-col>

            <v-col cols="12" md="11">
              <v-row>
                <v-col class="px-3">
                  <v-row no-gutters align="center">
                    <h5 class="greyBold--text font-weight-bold mb-0">{{ $t('settings/profile:profile.certificates') }}</h5>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs}">
                        <v-btn class="ml-2" icon @click="cardView = !cardView" v-bind="attrs" v-on="on">
                          <v-icon>list_alt</v-icon>
                        </v-btn>
                      </template>
                      <span v-if="cardView">{{ $t('buttons:table-view') }}</span>
                      <span v-else>{{ $t('buttons:card-view') }}</span>
                    </v-tooltip>
                  </v-row>
                </v-col>
                <!-- TODO add search field for cardView -->
                <v-spacer></v-spacer>
                <v-col class="text-right">
                  <BaseButton
                      color="primary"
                      :btn-text="$t('settings/profile:profile.add-docu')"
                      icon="add"
                      @clickedButton="addDocument()"
                  />

                  <UserDocumentationDialogForm ref="docuDialogForm" @updateDocus="updateDocuTable"/>
                </v-col>
                <v-col cols="12" class="pt-0" v-show="!cardView">
                  <DataTablePaginated
                      ref="tableDocuments"
                      class="ma-1"
                      :headers="headers"
                      @updateTableData="updateDocuTable"
                      icon="visibility"
                      icon2="cloud_download"
                      icon3="delete"
                      :iconTooltip="$t('buttons:show')"
                      :iconTooltip2="$t('buttons:download')"
                      :iconTooltip3="$t('buttons:delete')"
                      @clickedButton1="viewDocumentation"
                      @clickedButton2="downloadDocumentation"
                      @clickedButton3="deleteDocumentation"
                      :enabled-search="true"
                      sortBy="name"
                      @cacheCommit="$store.commit('settings/userProfile/storeDocumentationSearch', $event)"
                      :cachedSearch="$store.state.settings.userProfile.documentationSearch"
                  />

                  <DocumentViewer ref="documentViewer"/>
                </v-col>
              </v-row>
              <v-row v-show="cardView" class="px-5">
                <v-col cols="12" md="12" :lg="cardSize" xl="6" v-for="(item, i) in restDocu.content" :key="i" >
                  <ItemCard
                      :item="item"
                      :title="item.name"
                      titleExtra=""
                      :subtitle="$t('settings/profile:profile.item-cards.validity')
                         + ': ' + item.startDate + ' - ' + item.endDate"
                      :description="$t('common:role')
                         + ': ' + item.roleName"
                      @viewItem="viewDocumentation(item)"
                      hideActionBtn1
                      hideActionBtn2
                      hideActionBtn3
                      cardElevation
                      showDelete
                      @deleteItem="deleteDocumentation(item)"
                      extraIcon="cloud_download"
                      @extraIconAction="downloadDocumentation(item)"
                  />
                </v-col>
                <v-col v-if="restDocu.content.length == 0" class="text-center">
                  <h6 class="greyTint--text mb-0">
                    {{ $t('common:no-data-found') }}
                  </h6>
                </v-col>
              </v-row>

              <BaseButton
                  class="mt-5"
                  color="primary"
                  :btn-text="$t('buttons:save')"
                  @clickedButton="updateUser()"
              />

            <TwoButtonDialog
                ref="deleteDocumentationButton"
                :btn-text="$t('buttons:delete')"
                color="grey"
                :dialog-title="$t('dialog:delete-btn-title',{name : $t('settings/profile:profile.dialogs.part-1')})"
                :dialog-text="$t('dialog:delete-btn-dialog',{name : $t('settings/profile:profile.dialogs.part-2')})"
                :button1-text="$t('buttons:cancel')"
                :button2-text="$t('buttons:delete')"
                @clickedButton1="$refs.deleteDocumentationButton.showDialog = false"
                @clickedButton2="deleteDocumentationConfirmed()"
                :hide-button="true"
            />

            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>

import Users from "@/modules/core/data/users";

import DocumentationService from '@/modules/core/services/documentation-service';
import {ruleEmptyOrMinLength, ruleRequired} from '@/modules/core/services/rules';
import UsersService from '@/modules/core/services/users-service';
import Utils from '@/modules/core/services/utils';
import DataUtils from '@/modules/core/services/data-utils';

import DataTablePaginated from "@/modules/core/components/DataTable/DataTablePaginated";
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog"
import BaseButton from "@/modules/core/components/Base/BaseButton";
import ItemCard from '@/modules/core/components/ItemCard'
import UserDocumentationDialogForm from './UserDocumentationDialogForm'
import {DocumentType} from '@/modules/book/data/document-type.js';

import {mapState} from 'vuex'

export default {
  name: "UserProfile",
  components: {
    DataTablePaginated,
    DocumentViewer: () => import('@/modules/core/components/DocumentViewer/DocumentViewer'),
    BaseButton,
    ItemCard,
    TwoButtonDialog,
    UserDocumentationDialogForm
  },
  data() {
    return {
      data: new Users(),
      passwordShow: false,
      dataTable: {
        data: []
      },
      restDocu: {
        options: [],
        search: [],
        content: []
      },
      deleteDocumentationId: null,
      passwordChangeRoute: process.env.VUE_APP_AUTH_ACCOUNT_URL + '/password',
      currentTab: null,
      isFetchingUser: true,
      cardView: false,
    }
  },
  computed: {
    languages() {
      return [
        {value: "GERMAN", text: this.$t('enums:languages.german')},
        {value: "ENGLISH", text: this.$t('enums:languages.english')},
      ]
    },
    headers() {
      return [
        {text: this.$t("common:role"), value: 'roleName'},
        {text: this.$t('common:document-name'), value: 'name'},
        {text: this.$t('common:valid-from'), value: 'startDate'},
        {text: this.$t('common:valid-until'), value: 'endDate'},
        {text: "", value: 'controls'}
      ]
    },
    cardSize() {
      if(this.profileSettingsDrawer) {
        return '6'
      } else {
        return '4'
      }
    },
    ...mapState('settings/layout', {
      profileSettingsDrawer: state => state.profileSettingsDrawer
    }),
  },
  methods: {
    updateUser() {
      if (!this.$refs.form.validate()) {
        return;
      }

      UsersService.putProfileToAPI(this.data,
        () => {
          Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('settings/profile:profile.profile-settings')}));
          this.$router.push(this.$store.state.settings.routes.lastMainRoute);
          this.$router.go();
        },
        () => {
          Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('settings/profile:profile.profile-settings')}))
        }
      )
    },

    addDocument() {
      this.$refs.docuDialogForm.addDocu(this.restDocu.options, this.restDocu.search);
    },

    viewDocumentation(data) {
      this.$refs.documentViewer.initViewerById(this.restDocu.content, data.id, data.userId, DocumentType.USER);
    },

    downloadDocumentation(data) {
      DocumentationService.downloadFile(data.id, data.fileName, data.fileType, data.userId, data.documentType);
    },

    deleteDocumentation(item) {
      this.deleteDocumentationId = item.id;
      this.$refs.deleteDocumentationButton.showDialog = true;
    },

    deleteDocumentationConfirmed() {
      DocumentationService.delete(this.deleteDocumentationId,
        () => {
          this.updateDocuTable(this.restDocu.options, this.restDocu.search);
          Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('common:document')}));
        },
        (error) => {
          this.$logger.error("Failed to delete user documentation, because: " + error);
          Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('common:document')}))
        }
      )
      this.$refs.deleteDocumentationButton.showDialog = false;
    },

    updateDocuTable(options, search) {
      this.restDocu.options = options;
      this.restDocu.search = search;

      if(!search) search = '';
      this.$refs.tableDocuments.loading = true;
      DocumentationService.readPageableUserDocumentationsFromAPI(
        options.page - 1,
        options.itemsPerPage,
        search,
        options.sortBy,
        options.sortDesc,
        (resp) => {
          resp.data.content.forEach((document) => {
            document.type = this.$t('enums:facility-documentation-types.' + document.type)
          })
          this.restDocu.content = resp.data.content;

          if(this.$refs.tableDocuments !== undefined) {
            this.$refs.tableDocuments.fillTableData(resp.data);
          }
        },
        (error) => {
          this.$refs.tableDocuments.loading = false;
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:document_plural')}))
          this.$logger.error(error);
        }
      )
    },
    ruleEmptyOrMinLength,
    ruleRequired,
  },
  created() {
    UsersService.readProfileFromAPI(
      (resp) => {
        var response = DataUtils.parseDataToSingleObject(resp.data, Users);
        if (response == null) {
          this.$logger.error("Failed to receive user settings!");
          this.$router.go(-1);
        } else {
          this.data = response;
          this.isFetchingUser = false;
        }
      },
      (error) => {
        this.$logger.error(error);
        this.$router.push('/404')
      }
    )
  }
}

</script>
<style src="@/modules/settings/views/profile/Profile.scss" lang="scss"/>
